import React, { useEffect } from "react";
import "./CheckoutScreens.css";
import { formatRupees } from "../utils/money";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const timeNow = () => {
  const d = new Date();
  const h = d.getHours();
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

// These screens follow the Blue Tokai mobile app: Open Sans for text and a Brandon-style geometric face
// for headings. They are only needed once a customer pays, so they load then, not with the menu.
// (If you hold a web licence for Brandon Grotesque, add it as @font-face "Brandon Grotesque" and it wins.)
const APP_FONTS_HREF = "https://fonts.googleapis.com/css2?family=Jost:wght@500;600;700&family=Open+Sans:wght@400;600;700&display=swap";
function useAppFonts() {
  useEffect(() => {
    if (document.getElementById("bt-app-fonts")) return;
    const link = document.createElement("link");
    link.id = "bt-app-fonts";
    link.rel = "stylesheet";
    link.href = APP_FONTS_HREF;
    document.head.appendChild(link);
  }, []);
}

/** Shown over the cart while the payment is being opened / the order is being confirmed. */
export function PayingOverlay({ stage, showHighTrafficNotice }) {
  useAppFonts();
  return (
    <div className="checkout-overlay billing-ui bt-app" role="status" aria-live="polite">
      <div className="checkout-overlay-card">
        <span className="checkout-spinner" aria-hidden="true" />
        <p className="checkout-overlay-title">{stage === "confirming" ? "Confirming your order…" : "Opening payment…"}</p>
        <p className="checkout-overlay-sub">
          {stage === "confirming" ? "Please keep this screen open." : "Complete the payment in the window that opens."}
        </p>
        {showHighTrafficNotice && stage === "confirming" && (
          <div className="bt-notice">
            <p className="bt-notice-title">What's happening?</p>
            <p className="bt-notice-text">
              We're facing high traffic on our backend. Your payment is initiated; we'll notify you as soon as it is verified. Please keep this screen open.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Page shell shared by the result screens: the mobile app's plain white page, one centred column.
function ResultScreen({ children, label }) {
  useAppFonts();
  return (
    <div className="bt-screen billing-ui bt-app" role="dialog" aria-modal="true" aria-label={label}>
      <div className="bt-screen-inner">{children}</div>
    </div>
  );
}

export function PaymentIssueScreen({ reason, onTryAgain }) {
  const refunded = /refund|returned/i.test(reason);
  return (
    <ResultScreen label="Payment issue">
      <div className="bt-hero">
        <div className="bt-badge bt-badge-warn" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="38" height="38" fill="none">
            <path d="M12 6.5v7" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" />
            <circle cx="12" cy="17.6" r="1.6" fill="currentColor" />
          </svg>
        </div>
        <h1 className="bt-title">{refunded ? "Order couldn't be placed" : "Payment issue"}</h1>
        <p className="bt-sub">
          {reason}
          {refunded ? "" : " Please contact support before trying again — if you were charged, it will be refunded."}
        </p>
      </div>
      <button type="button" className="bt-btn-primary" onClick={onTryAgain}>Try Again</button>
    </ResultScreen>
  );
}

export function OrderPlacedScreen({
  orderResult, cafeName, tableNumber, orderType, items = [], itemsTotal, rewardName, rewardAmount = 0,
  discountName, discountAmount = 0, gst = 0, amount, isFree, onDone,
}) {
  const success = orderResult.ristaPushStatus === "SUCCESS";
  const saved = (rewardAmount || 0) + (discountAmount || 0);
  const shown = items.slice(0, 4);
  const more = Math.max(0, items.length - shown.length);
  const isTakeaway = orderType === "takeaway";
  // Same plain tone as the app's "Payment success!" screen — what happened, where, and a thank-you.
  const placedAt = cafeName ? ` at ${cafeName}` : "";
  const where = isTakeaway
    ? " We'll pack it for takeaway."
    : tableNumber ? ` We'll bring it to Table #${tableNumber}.` : "";

  return (
    <ResultScreen label="Order confirmation">
      <div className="bt-hero">
        <img className="bt-cup" src="/app-success-cup.png" width="84" height="92" alt="" aria-hidden="true" draggable={false} />
        <h1 className="bt-title">{success ? (isFree ? "Order confirmed!" : "Order placed!") : "Payment confirmed"}</h1>
        <p className="bt-sub">
          {success
            ? `Your order has been successfully placed${placedAt}.${where}`
            : "We're still placing your order. Please show this screen to the barista — it updates by itself once it's placed."}
          {success && <><br />Many thanks from the Blue Tokai team!</>}
        </p>
      </div>

      {/* same two-up summary box as the app's "Amount Paid / Points Earned" */}
      <div className="bt-stats" role="group" aria-label="Order summary">
        <div className="bt-stat">
          <strong>₹{formatRupees(amount)}</strong>
          <span>Amount paid</span>
        </div>
        <div className="bt-stat-divider" aria-hidden="true" />
        <div className="bt-stat">
          <strong>{orderResult.invoiceNumber ? `#${orderResult.invoiceNumber}` : "Placing…"}</strong>
          <span>Order no.</span>
        </div>
      </div>

      <section className="bt-receipt" aria-label="Order receipt">
        <p className="bt-receipt-meta">
          {cafeName && <span className="bt-meta-cafe">{cafeName}</span>}
          {[isTakeaway ? "Takeaway" : (tableNumber && `Table #${tableNumber}`), timeNow()].filter(Boolean).map((part) => (
            <span key={part} className="bt-meta-part">{part}</span>
          ))}
        </p>

        {shown.length > 0 && (
          <ul className="bt-receipt-items">
            {shown.map((item) => (
              <li key={item.id}>
                <span className="bt-qty">{item.quantity}×</span>
                <span className="bt-item-name">{item.name}</span>
                <span className="bt-item-price">₹{formatRupees(item.unitPrice * item.quantity)}</span>
              </li>
            ))}
            {more > 0 && <li className="bt-more">+ {more} more item{more === 1 ? "" : "s"}</li>}
          </ul>
        )}

        <dl className="bt-bill">
          <div className="bt-bill-row"><dt>Items Total</dt><dd>₹{formatRupees(itemsTotal)}</dd></div>
          {rewardAmount > 0 && <div className="bt-bill-row bt-bill-good"><dt>{rewardName || "Reward"} applied</dt><dd>−₹{formatRupees(rewardAmount)}</dd></div>}
          {discountAmount > 0 && <div className="bt-bill-row bt-bill-good"><dt>{discountName || "Offer"} applied</dt><dd>−₹{formatRupees(discountAmount)}</dd></div>}
          <div className="bt-bill-row"><dt>GST</dt><dd>+₹{formatRupees(gst)}</dd></div>
          <div className="bt-bill-total"><dt>{isFree ? "Total" : "Total paid"}</dt><dd>₹{formatRupees(amount)}</dd></div>
        </dl>
        {saved > 0 && <p className="bt-saved">You saved ₹{formatRupees(saved)} on this order</p>}
        {isFree && <p className="bt-saved bt-saved-soft">Fully covered by your Circle reward</p>}
      </section>

      <button type="button" className="bt-btn-primary" onClick={onDone}>Done</button>
    </ResultScreen>
  );
}
